import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { TextField, MenuItem, InputAdornment } from "@mui/material";
import {
  useFetchUnitsForSelectionQuery,
  useCreateCompositeUnitMutation,
  useUpdateUnitMutation,
} from "../../../../../services/baseQuery";
import { toast } from "react-hot-toast";

export const CompositeUnitForm = ({ onClose, initialData, isEditing }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: initialData || {
      name: "",
      abbreviation: "",
      smallestUnit: "",
      largerUnit: "",
      conversionFactor: "",
      description: "",
    },
  });

  const { data: unitsData } = useFetchUnitsForSelectionQuery();
  const [createUnit, { isLoading: isCreating }] =
    useCreateCompositeUnitMutation();
  const [updateUnit, { isLoading: isUpdating }] = useUpdateUnitMutation();

  const isLoading = isCreating || isUpdating;

  const baseUnits = unitsData?.data?.baseUnits || [];

  // Watch for changes in form fields
  const selectedLargerUnit = watch("largerUnit"); // From unit (larger)
  const selectedSmallerUnit = watch("smallestUnit"); // To unit (smaller)
  const conversionFactor = watch("conversionFactor");

  // Find unit details
  const largerUnitDetails =
    baseUnits.find(u => u._id === selectedLargerUnit) || null;
  const smallerUnitDetails =
    baseUnits.find(u => u._id === selectedSmallerUnit) || null;

  // Update the useEffect to handle the auto-generation more robustly
  useEffect(() => {
    console.log(
      largerUnitDetails,
      smallerUnitDetails,
      conversionFactor,
      "<<<<< conversionFactor",
    );

    if (largerUnitDetails && smallerUnitDetails && conversionFactor) {
      try {
        // Format the conversion factor to remove trailing zeros
        const formattedFactor = Number(conversionFactor).toString();

        // Generate name and abbreviation
        const newName = `1${largerUnitDetails.name}/${formattedFactor}${smallerUnitDetails.name}`;
        const newAbbreviation = `${largerUnitDetails.abbreviation}/${formattedFactor}${smallerUnitDetails.abbreviation}`;

        setValue("name", newName);
        setValue("abbreviation", newAbbreviation);
      } catch (error) {
        console.error("Error generating unit names:", error);
      }
    }
  }, [largerUnitDetails, smallerUnitDetails, conversionFactor, setValue]);

  // When editing, we need to set up the form with existing values
  useEffect(() => {
    if (isEditing && initialData) {
      console.log("Initializing composite form with:", initialData);

      // If we're editing, we need to make sure the form is populated
      if (
        initialData.smallestUnit &&
        initialData.largerUnit &&
        initialData.conversionFactor
      ) {
        // Find the units
        const smallerUnitDetails = baseUnits.find(
          u => u._id === initialData.smallestUnit,
        );
        const largerUnitDetails = baseUnits.find(
          u => u._id === initialData.largerUnit,
        );

        if (smallerUnitDetails && largerUnitDetails) {
          // Set the values manually to ensure they're properly displayed
          setValue("name", initialData.name);
          setValue("abbreviation", initialData.abbreviation);
          setValue("smallestUnit", initialData.smallestUnit);
          setValue("largerUnit", initialData.largerUnit);
          setValue("conversionFactor", initialData.conversionFactor);
          setValue("description", initialData.description || "");
        }
      }
    }
  }, [isEditing, initialData, setValue, baseUnits]);

  const onSubmit = async formData => {
    try {
      if (isEditing) {
        await updateUnit({
          id: initialData.id,
          data: {
            name: formData.name,
            abbreviation: formData.abbreviation,
            smallestUnitId: formData.smallestUnit,
            largerUnitId: formData.largerUnit,
            conversionFactor: parseFloat(formData.conversionFactor),
            description: formData.description,
          },
        });
        toast.success("Unit updated successfully");
      } else {
        await createUnit({
          name: formData.name,
          abbreviation: formData.abbreviation,
          smallestUnitId: formData.smallestUnit,
          largerUnitId: formData.largerUnit,
          conversionFactor: parseFloat(formData.conversionFactor),
          description: formData.description,
        }).unwrap();
        toast.success("Composite unit created successfully");
      }
      onClose();
    } catch (error) {
      console.log(error, "<<<<< error");
      toast.error(error?.error || "Failed to save unit");
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.5rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E5E7EB",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#5652B0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#5652B0",
    },
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
      <div className="grid gap-6">
        {/* Unit Selection Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            Unit Conversion Setup
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="largerUnit"
              control={control}
              rules={{ required: "Larger unit is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Larger Unit"
                  error={!!errors.largerUnit}
                  helperText={
                    errors.largerUnit?.message ||
                    "Select the larger unit (e.g., PACK)"
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={inputStyles}>
                  {baseUnits.map(unit => (
                    <MenuItem key={unit._id} value={unit._id}>
                      {unit.displayName ||
                        `${unit.name} (${unit.abbreviation})`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="smallestUnit"
              control={control}
              rules={{ required: "Smaller unit is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Smaller Unit"
                  error={!!errors.baseUnit}
                  helperText={
                    errors.smallestUnit?.message ||
                    "Select the smaller unit (e.g., PAIR)"
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={inputStyles}>
                  {baseUnits.map(unit => (
                    <MenuItem key={unit._id} value={unit._id}>
                      {unit.displayName ||
                        `${unit.name} (${unit.abbreviation})`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>

          <div className="mt-4">
            <TextField
              type="number"
              label="Number of Smaller Units"
              {...register("conversionFactor", {
                required: "Number of units is required",
                min: { value: 0.000001, message: "Must be greater than 0" },
              })}
              error={!!errors.conversionFactor}
              helperText={
                errors.conversionFactor?.message ||
                "How many smaller units make one larger unit?"
              }
              variant="outlined"
              size="small"
              fullWidth
              sx={inputStyles}
            />
          </div>

          {/* Conversion Preview */}
          {largerUnitDetails && smallerUnitDetails && conversionFactor && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                1 {largerUnitDetails.name} = {conversionFactor}{" "}
                {smallerUnitDetails.name}
              </p>
            </div>
          )}
        </div>

        {/* Generated Name and Abbreviation */}
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Generated Name"
            {...register("name")}
            disabled
            value={watch("name")}
            variant="outlined"
            size="small"
            fullWidth
            sx={inputStyles}
          />

          <TextField
            label="Generated Abbreviation"
            {...register("abbreviation")}
            disabled
            value={watch("abbreviation")}
            variant="outlined"
            size="small"
            fullWidth
            sx={inputStyles}
          />
        </div>

        <TextField
          label="Description (Optional)"
          {...register("description")}
          multiline
          rows={2}
          variant="outlined"
          size="small"
          fullWidth
          sx={inputStyles}
          placeholder="Add any additional notes about this unit conversion"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          disabled={isLoading}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            isLoading ||
            !selectedLargerUnit ||
            !selectedSmallerUnit ||
            !conversionFactor
          }
          className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            `${isEditing ? "Update" : "Create"} Composite Unit`
          )}
        </button>
      </div>
    </form>
  );
};
